import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { Reveal } from "../components/motion/Reveal";

export default function CVPage() {
  const [cv, setCv] = useState(null);

  useEffect(() => {
    api
      .get("/cv")
      .then((res) => setCv(res.data))
      .catch(() => {});
  }, []);

  useSeo({
    title: cv?.page?.seo_title || "Johnny Depp — Artist CV | Pantheon Art",
    description: cv?.page?.seo_description || "Artist CV for Johnny Depp, managed by Pantheon Art.",
    path: "/johnny-depp/cv",
    image: cv?.page?.quote_image,
  });

  const sections = [];
  (cv?.entries || []).forEach((entry) => {
    let section = sections.find((s) => s.name === entry.section);
    if (!section) {
      section = { name: entry.section, entries: [] };
      sections.push(section);
    }
    section.entries.push(entry);
  });

  const biographyParagraphs = (cv?.page?.biography || "").split(/\n\s*\n/).filter(Boolean);

  return (
    <div data-testid="cv-page">
      <div className="px-5 pt-32 lg:px-10 lg:pt-40">
        <Reveal>
          <h1 className="h-condensed text-[clamp(2.5rem,6vw,5.5rem)]">
            Johnny Depp
            <span className="h-serif block text-[clamp(1.75rem,4vw,3.5rem)]">Artist CV</span>
          </h1>
        </Reveal>
      </div>

      <div className="mt-14 grid lg:mt-20 lg:grid-cols-2" data-testid="cv-split">
        <div className="px-5 pb-16 lg:border-r lg:px-10 lg:pb-24" style={{ borderColor: "var(--charcoal)" }}>
          <Reveal>
            <p className="eyebrow text-[0.65rem] opacity-60">Born Kentucky, United States, 1963</p>
          </Reveal>
          <div className="mt-10 space-y-12">
            {sections.map((section, si) => (
              <Reveal key={section.name} delay={Math.min(si * 0.05, 0.3)}>
                <section className={si > 0 ? "hairline-t pt-10" : ""} data-testid={`cv-section-${si}`}>
                  <h2 className="eyebrow text-xs">{section.name}</h2>
                  <dl className="mt-6 space-y-4">
                    {section.entries.map((entry) => (
                      <div key={entry.id} className="flex gap-6">
                        <dt className="w-16 shrink-0 font-condensed text-sm font-semibold uppercase tracking-[0.08em]">
                          {entry.year}
                        </dt>
                        <dd className="text-sm font-light leading-relaxed">{entry.text}</dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="hairline-t px-5 py-16 lg:border-t-0 lg:px-10 lg:py-0 lg:pb-24" data-testid="cv-biography">
          <Reveal>
            <h2 className="eyebrow text-xs lg:sticky lg:top-24">Artist Biography</h2>
          </Reveal>
          <div className="body-text mt-8 space-y-6 text-base">
            {biographyParagraphs.map((p, i) => (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.25)}>
                <p>{p}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      {cv?.page?.quote && (
        <section className="hairline-t grid lg:grid-cols-2" data-testid="cv-quote-section">
          <div className="flex items-center px-5 py-16 lg:min-h-[70vh] lg:px-14 lg:py-24">
            <Reveal>
              <blockquote>
                <p className="serif-italic text-[clamp(1.6rem,2.8vw,2.75rem)] leading-[1.25]" style={{ textTransform: "none" }}>
                  “{cv.page.quote}”
                </p>
                <cite className="eyebrow mt-8 block text-[0.65rem] not-italic opacity-60">Johnny Depp</cite>
              </blockquote>
            </Reveal>
          </div>
          <div className="relative min-h-[50vh] overflow-hidden lg:min-h-[70vh]">
            {cv?.page?.quote_image && (
              <img
                src={cv.page.quote_image}
                alt={cv.page.quote_image_alt || "Johnny Depp working in his studio"}
                loading="lazy"
                className="kenburns absolute inset-0 h-full w-full object-cover"
              />
            )}
          </div>
        </section>
      )}
    </div>
  );
}
